<template>
  <div class="trading-rates-page">
    <div class="page-header">
      <div>
        <h1>Торговые ставки</h1>
        <p>Фиксированные ставки для ботов. Приоритет: фиксированная &gt; память &gt; ATI. Звёздочка (*) = любой.</p>
      </div>
      <div class="status-panel">
        <span :class="{'status-dot': true, 'status-dot-loading': loading}"></span>
        {{ loading ? 'Загрузка...' : (editingId ? 'Режим редактирования' : 'Просмотр') }}
      </div>
    </div>

    <section class="card summary">
      <div class="metric">
        <span>Всего ставок</span>
        <strong>{{ totalItems }}</strong>
      </div>
      <div class="metric">
        <span>Страница</span>
        <strong>{{ currentPage }} / {{ totalPages }}</strong>
      </div>
    </section>

    <section class="card">
      <div class="toolbar">
        <input
          v-model="search"
          class="search-input"
          placeholder="Поиск по городу/сервису"
          @keyup.enter="applySearch"
        >
        <div class="toolbar-actions">
          <input
            ref="fileInput"
            class="file-input"
            type="file"
            accept=".xlsx"
            @change="handleFileChange"
          >
          <button v-if="canWrite" class="btn-secondary" :disabled="loading || importing || exporting || editingId" @click="triggerImport">
            {{ importing ? 'Загружаем...' : 'Загрузить Excel' }}
          </button>
          <button class="btn-secondary" :disabled="loading || importing || exporting || editingId" @click="exportRates">
            {{ exporting ? 'Выгружаем...' : 'Выгрузить Excel' }}
          </button>
          <button class="btn-secondary" :disabled="loading || editingId" @click="loadRates">
            {{ loading ? 'Обновляем...' : 'Обновить' }}
          </button>
          <button v-if="canWrite" class="btn-primary" :disabled="loading || editingId" @click="startCreate">
            Добавить строку
          </button>
        </div>
      </div>

      <div v-if="hasError" class="error-message">
        Не удалось загрузить торговые ставки.
      </div>
      <div v-if="importMessage" :class="importMessageType === 'error' ? 'error-message' : 'success-message'">
        {{ importMessage }}
      </div>

      <div v-if="!hasError" class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Сервис</th>
              <th>Тип тр-та</th>
              <th>1 точка</th>
              <th>2 точка</th>
              <th class="col-money">Без НДС</th>
              <th class="col-money">С НДС</th>
              <th class="col-version">Вер.</th>
              <th>Обновлено</th>
              <th>Кем</th>
              <th class="col-actions">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && items.length === 0">
              <td :colspan="10" class="empty-cell">Загрузка...</td>
            </tr>
            <tr v-else-if="items.length === 0 && !isCreating">
              <td :colspan="10" class="empty-cell">Ставок не найдено</td>
            </tr>
            <tr v-if="isCreating" class="editing">
              <td>
                <select v-model="editForm.service_name" class="table-input">
                  <option value="">* (любой)</option>
                  <option v-for="s in serviceOptions" :key="s" :value="s">{{ s }}</option>
                </select>
              </td>
              <td>
                <select v-model="editForm.transport_type" class="table-input">
                  <option value="">* (любой)</option>
                  <option v-for="t in transportOptions" :key="t" :value="t">{{ t }}</option>
                </select>
              </td>
              <td><input v-model="editForm.from_city" class="table-input" placeholder="* = любой"></td>
              <td><input v-model="editForm.to_city" class="table-input" placeholder="* = любой"></td>
              <td><input v-model.number="editForm.cost_without_nds" class="table-input input-narrow" type="number" min="1"></td>
              <td><input v-model.number="editForm.cost_with_nds" class="table-input input-narrow" type="number" min="1"></td>
              <td>1</td>
              <td>-</td>
              <td>-</td>
              <td class="actions-cell">
                <button class="btn-save" :disabled="saving" @click.stop="saveCreate">{{ saving ? '...' : 'Создать' }}</button>
                <button class="btn-cancel" :disabled="saving" @click.stop="cancelEdit">Отмена</button>
              </td>
            </tr>
            <tr
              v-for="item in items"
              :key="item.id"
              :class="{ editing: editingId === item.id, clickable: !editingId && canWrite }"
              @click="onRowClick(item)"
            >
              <td>
                <select v-if="editingId === item.id" v-model="editForm.service_name" class="table-input">
                  <option value="">* (любой)</option>
                  <option v-for="s in serviceOptions" :key="s" :value="s">{{ s }}</option>
                </select>
                <span v-else>{{ item.service_name || '*' }}</span>
              </td>
              <td>
                <select v-if="editingId === item.id" v-model="editForm.transport_type" class="table-input">
                  <option value="">* (любой)</option>
                  <option v-for="t in transportOptions" :key="t" :value="t">{{ t }}</option>
                </select>
                <span v-else>{{ item.transport_type || '*' }}</span>
              </td>
              <td>
                <input v-if="editingId === item.id" v-model="editForm.from_city" class="table-input" placeholder="* = любой">
                <span v-else>{{ item.from_city || '*' }}</span>
              </td>
              <td>
                <input v-if="editingId === item.id" v-model="editForm.to_city" class="table-input" placeholder="* = любой">
                <span v-else>{{ item.to_city || '*' }}</span>
              </td>
              <td>
                <input v-if="editingId === item.id" v-model.number="editForm.cost_without_nds" class="table-input input-narrow" type="number" min="1">
                <span v-else class="money-cell">{{ formatMoney(item.cost_without_nds) }}</span>
              </td>
              <td>
                <input v-if="editingId === item.id" v-model.number="editForm.cost_with_nds" class="table-input input-narrow" type="number" min="1">
                <span v-else class="money-cell">{{ formatMoney(item.cost_with_nds) }}</span>
              </td>
              <td class="col-version">{{ item.version }}</td>
              <td class="col-date">{{ formatDate(item.updated_at || item.created_at) }}</td>
              <td class="col-editor">{{ item.updated_by || '-' }}</td>
              <td class="actions-cell">
                <template v-if="editingId === item.id">
                  <button class="btn-save" :disabled="saving" @click.stop="saveEdit(item)">{{ saving ? '...' : 'Сохранить' }}</button>
                  <button class="btn-cancel" :disabled="saving" @click.stop="cancelEdit">Отмена</button>
                </template>
                <template v-else-if="canWrite">
                  <button class="btn-edit" :disabled="editingId || deletingId === item.id" @click.stop="startEdit(item)">Изменить</button>
                  <button class="btn-delete" :disabled="editingId || deletingId === item.id" @click.stop="deleteRate(item)">
                    {{ deletingId === item.id ? '...' : 'Удалить' }}
                  </button>
                </template>
                <template v-else>
                  <span class="money-cell">—</span>
                </template>
              </td>
            </tr>
            <tr v-if="rowError">
              <td :colspan="10" class="row-error">{{ rowError }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <footer class="pagination">
      <button :disabled="currentPage <= 1 || loading || editingId" @click="changePage(currentPage - 1)">Назад</button>
      <button :disabled="currentPage >= totalPages || loading || editingId" @click="changePage(currentPage + 1)">Вперед</button>
    </footer>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { createTradingRate, deleteTradingRate, exportTradingRates, getTradingRates, importTradingRates, updateTradingRate } from '@/backend/tradingRatesApi'
import { downloadBlobResponse } from '@/backend/download'

const NEW_ROW_ID = '__new__'
const WILDCARD_VALUES = ['', '*', null]

export default {
  name: 'TradingRatesPage',
  setup() {
    const store = useStore()
    const canWrite = computed(() => store.getters.hasPermission('memory.trading_rates.write'))
    const serviceOptions = ['efes', 'loginet', 'trucker', 'unitedbrew', 'efko', 'artlogic', 'docrobot']
    const transportOptions = ['tent', 'ref', 'iso']

    const items = ref([])
    const search = ref('')
    const currentPage = ref(1)
    const totalPages = ref(1)
    const totalItems = ref(0)
    const loading = ref(false)
    const saving = ref(false)
    const importing = ref(false)
    const exporting = ref(false)
    const deletingId = ref(null)
    const hasError = ref(false)
    const editingId = ref(null)
    const isCreating = ref(false)
    const editForm = ref({})
    const rowError = ref('')
    const importMessage = ref('')
    const importMessageType = ref('success')
    const fileInput = ref(null)
    let searchTimer = null

    watch(search, () => {
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        currentPage.value = 1
        loadRates()
      }, 400)
    })

    const loadRates = async () => {
      if (loading.value || editingId.value) return
      loading.value = true
      try {
        const data = await getTradingRates({
          page: currentPage.value,
          per_page: 50,
          search: search.value || undefined,
        })
        items.value = Array.isArray(data.items) ? data.items : []
        currentPage.value = data.current_page || 1
        totalPages.value = data.total_pages || 1
        totalItems.value = data.total_items || 0
        hasError.value = false
      } catch (error) {
        hasError.value = true
      } finally {
        loading.value = false
      }
    }

    const applySearch = () => {
      currentPage.value = 1
      loadRates()
    }

    const changePage = (page) => {
      currentPage.value = page
      loadRates()
    }

    const triggerImport = () => {
      if (!canWrite.value || loading.value || importing.value || exporting.value || editingId.value) return
      fileInput.value?.click()
    }

    const handleFileChange = async (event) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file || !canWrite.value) return

      importing.value = true
      importMessage.value = ''
      rowError.value = ''
      try {
        const result = await importTradingRates(file)
        importMessageType.value = result.skipped ? 'error' : 'success'
        importMessage.value = `Excel обработан: создано ${result.created}, обновлено ${result.updated}, пропущено ${result.skipped}.`
        if (Array.isArray(result.errors) && result.errors.length) {
          importMessage.value += ` Ошибки: ${result.errors.slice(0, 5).join('; ')}`
        }
        currentPage.value = 1
        await loadRates()
      } catch (error) {
        importMessageType.value = 'error'
        if (typeof error.response?.data?.detail === 'string') {
          importMessage.value = error.response.data.detail
        } else {
          importMessage.value = 'Не удалось загрузить Excel-файл'
        }
      } finally {
        importing.value = false
      }
    }

    const exportRates = async () => {
      if (loading.value || importing.value || exporting.value || editingId.value) return
      exporting.value = true
      importMessage.value = ''
      rowError.value = ''
      try {
        const response = await exportTradingRates({ search: search.value || undefined })
        await downloadBlobResponse(response, 'trading_rates.xlsx')
      } catch (error) {
        importMessageType.value = 'error'
        importMessage.value = error.message || 'Не удалось выгрузить Excel-файл'
      } finally {
        exporting.value = false
      }
    }

    const onRowClick = (item) => {
      if (!canWrite.value || editingId.value || deletingId.value) return
      startEdit(item)
    }

    const startEdit = (item) => {
      rowError.value = ''
      editingId.value = item.id
      editForm.value = {
        ati_route_id: item.ati_route_id,
        service_name: item.service_name || '',
        transport_type: item.transport_type || '',
        from_city: item.from_city || '',
        to_city: item.to_city || '',
        cost_without_nds: item.cost_without_nds,
        cost_with_nds: item.cost_with_nds,
        version: item.version,
      }
    }

    const startCreate = () => {
      if (!canWrite.value) return
      rowError.value = ''
      isCreating.value = true
      editingId.value = NEW_ROW_ID
      editForm.value = {
        ati_route_id: null,
        service_name: '',
        transport_type: '',
        from_city: '',
        to_city: '',
        cost_without_nds: null,
        cost_with_nds: null,
      }
    }

    const cancelEdit = () => {
      editingId.value = null
      isCreating.value = false
      editForm.value = {}
      rowError.value = ''
    }

    const isWildcard = (val) => WILDCARD_VALUES.includes(val)

    const validateForm = () => {
      const form = editForm.value
      if (!Number(form.cost_without_nds) || Number(form.cost_without_nds) <= 0) return 'Цена без НДС должна быть положительной'
      if (!Number(form.cost_with_nds) || Number(form.cost_with_nds) <= 0) return 'Цена с НДС должна быть положительной'
      if (Number(form.cost_without_nds) > Number(form.cost_with_nds)) return 'Цена без НДС должна быть меньше или равна цене с НДС'

      if (
        isWildcard(form.service_name) &&
        isWildcard(form.transport_type) &&
        isWildcard(form.from_city) &&
        isWildcard(form.to_city)
      ) {
        return 'Хотя бы одно измерение должно быть конкретным (не *)'
      }

      return ''
    }

    const normalizeWildcard = (val) => {
      const trimmed = String(val || '').trim()
      return isWildcard(trimmed) ? null : trimmed
    }

    const createPayload = () => ({
      ati_route_id: editForm.value.ati_route_id || null,
      service_name: normalizeWildcard(editForm.value.service_name),
      transport_type: normalizeWildcard(editForm.value.transport_type),
      from_city: normalizeWildcard(editForm.value.from_city),
      to_city: normalizeWildcard(editForm.value.to_city),
      cost_without_nds: Number(editForm.value.cost_without_nds),
      cost_with_nds: Number(editForm.value.cost_with_nds),
    })

    const saveCreate = async () => {
      rowError.value = validateForm()
      if (rowError.value) return

      saving.value = true
      try {
        const created = await createTradingRate(createPayload())
        items.value.unshift(created)
        totalItems.value += 1
        cancelEdit()
      } catch (error) {
        if (typeof error.response?.data?.detail === 'string') {
          rowError.value = error.response.data.detail
        } else {
          rowError.value = 'Не удалось создать строку'
        }
      } finally {
        saving.value = false
      }
    }

    const saveEdit = async (item) => {
      rowError.value = validateForm()
      if (rowError.value) return

      saving.value = true
      try {
        const updated = await updateTradingRate(item.id, {
          ati_route_id: editForm.value.ati_route_id || null,
          service_name: normalizeWildcard(editForm.value.service_name),
          transport_type: normalizeWildcard(editForm.value.transport_type),
          from_city: normalizeWildcard(editForm.value.from_city),
          to_city: normalizeWildcard(editForm.value.to_city),
          cost_without_nds: Number(editForm.value.cost_without_nds),
          cost_with_nds: Number(editForm.value.cost_with_nds),
          version: editForm.value.version,
        })
        const index = items.value.findIndex((rate) => rate.id === item.id)
        if (index !== -1) items.value.splice(index, 1, updated)
        cancelEdit()
      } catch (error) {
        if (error.response?.status === 409) {
          rowError.value = 'Строка была изменена на сервере. Отмените редактирование и обновите список.'
        } else if (typeof error.response?.data?.detail === 'string') {
          rowError.value = error.response.data.detail
        } else {
          rowError.value = 'Не удалось сохранить строку'
        }
      } finally {
        saving.value = false
      }
    }

    const deleteRate = async (item) => {
      if (!canWrite.value || editingId.value || deletingId.value) return
      const label = [
        item.service_name || '*',
        [item.from_city || '*', item.to_city || '*'].join('-'),
      ].join(' ')
      const confirmed = window.confirm(`Удалить торговую ставку "${label}"?`)
      if (!confirmed) return

      deletingId.value = item.id
      rowError.value = ''
      try {
        await deleteTradingRate(item.id)
        items.value = items.value.filter((rate) => rate.id !== item.id)
        totalItems.value = Math.max(totalItems.value - 1, 0)
        if (items.value.length === 0 && currentPage.value > 1) {
          currentPage.value -= 1
          await loadRates()
        }
      } catch (error) {
        if (typeof error.response?.data?.detail === 'string') {
          rowError.value = error.response.data.detail
        } else {
          rowError.value = 'Не удалось удалить строку'
        }
      } finally {
        deletingId.value = null
      }
    }

    const formatMoney = (value) => {
      if (!value) return '-'
      return new Intl.NumberFormat('ru-RU').format(value)
    }

    const formatDate = (value) => {
      if (!value) return '-'
      return new Date(value).toLocaleString('ru-RU')
    }

    onMounted(loadRates)

    return {
      canWrite,
      serviceOptions,
      transportOptions,
      items,
      search,
      currentPage,
      totalPages,
      totalItems,
      loading,
      saving,
      importing,
      exporting,
      deletingId,
      hasError,
      editingId,
      isCreating,
      editForm,
      rowError,
      importMessage,
      importMessageType,
      fileInput,
      loadRates,
      applySearch,
      changePage,
      triggerImport,
      handleFileChange,
      exportRates,
      onRowClick,
      startCreate,
      startEdit,
      cancelEdit,
      saveCreate,
      saveEdit,
      deleteRate,
      formatMoney,
      formatDate,
    }
  },
}
</script>

<style scoped>
.trading-rates-page {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 24px;
  color: #1f2937;
}

.page-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.page-header h1 {
  color: #1f2937;
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 4px;
}

.page-header p {
  color: #6b7280;
  font-size: 13px;
}

.status-panel {
  align-items: center;
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  color: #1d4ed8;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  padding: 8px 12px;
  white-space: nowrap;
}

.status-dot {
  background: #22c55e;
  border-radius: 50%;
  display: block;
  height: 8px;
  width: 8px;
}

.status-dot-loading {
  animation: pulse 1s infinite;
  background: #2563eb;
}

@keyframes pulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  margin-bottom: 16px;
  padding: 18px;
}

.summary {
  display: flex;
  gap: 12px;
}

.summary .metric {
  background: #f9fafb;
  border-radius: 10px;
  padding: 10px 14px;
}

.summary .metric span {
  color: #6b7280;
  display: block;
  font-size: 11px;
  margin-bottom: 3px;
}

.summary .metric strong {
  color: #111827;
  font-size: 15px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.search-input {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #111827;
  font-size: 13px;
  padding: 8px 10px;
  width: 260px;
}

.error-message,
.row-error,
.success-message {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
}

.row-error {
  text-align: center;
}

.success-message {
  background: #dcfce7;
  border-color: #bbf7d0;
  color: #166534;
}

.file-input {
  display: none;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  border: 1px solid #e5e7eb;
  border-collapse: separate;
  border-radius: 12px;
  border-spacing: 0;
  width: 100%;
  min-width: 900px;
}

.data-table th,
.data-table td {
  border-bottom: 1px solid #e5e7eb;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
  font-size: 13px;
}

.data-table th + th,
.data-table td + td {
  border-left: 1px solid #f1f5f9;
}

.data-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
  white-space: nowrap;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:nth-child(even) {
  background: #fbfdff;
}

.data-table tbody tr:hover {
  background: #eef4ff;
}

.data-table tr.clickable {
  cursor: pointer;
}

.data-table tr.editing {
  background: #fffbeb;
}

.col-money {
  text-align: right;
  white-space: nowrap;
}

.col-version {
  text-align: center;
  white-space: nowrap;
  color: #6b7280;
}

.col-date {
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}

.col-editor {
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}

.col-actions {
  text-align: right;
}

.money-cell {
  font-weight: 700;
  color: #111827;
}

.empty-cell {
  text-align: center !important;
  color: #6b7280;
  padding: 24px !important;
}

.table-input {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
  font-size: 13px;
  padding: 6px 8px;
  width: 100%;
  min-width: 100px;
}

.input-narrow {
  min-width: 80px;
  max-width: 100px;
}

.actions-cell {
  white-space: nowrap;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

button {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
  padding: 6px 10px;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-edit {
  background: #eef4ff;
  color: #1d4ed8;
}

.btn-save {
  background: #16a34a;
  color: #fff;
}

.btn-cancel {
  background: #f1f5f9;
  color: #475569;
}

.btn-delete {
  background: #fee2e2;
  color: #991b1b;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.pagination button {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #475569;
  padding: 8px 14px;
}

.pagination button:disabled {
  opacity: 0.4;
}

@media (max-width: 900px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-actions {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }

  .summary {
    flex-direction: column;
  }
}
</style>
